/**
 * Portal Router
 *
 * Routes for portal.claimshieldpro.com
 * All routes require authentication.
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { PortalLayout, LoginView, ForgotPasswordView, ResetPasswordView, NotFoundView, setupGuards, useAuthStore } from '@claimshield/shared'

const routes: RouteRecordRaw[] = [
  // Auth routes (public)
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Sign In | ClaimShield Portal' },
  },

  // Onboarding routes (public - token-based auth, self-contained layout)
  {
    path: '/proposal/:token',
    name: 'proposal',
    component: () => import('@views/ProposalView.vue'),
    meta: { requiresAuth: false, title: 'Proposal | ClaimShield' },
  },
  {
    path: '/sign-agreement/:token',
    name: 'sign-agreement',
    component: () => import('@views/SignAgreementView.vue'),
    meta: { requiresAuth: false, title: 'Sign Agreement | ClaimShield' },
  },
  {
    path: '/eft/:token',
    name: 'eft',
    redirect: to => ({ name: 'proposal', params: { token: to.params.token } }),
  },
  {
    path: '/booking/:token',
    name: 'booking',
    component: () => import('@views/BookingView.vue'),
    meta: { requiresAuth: false, title: 'Book Welcome Call | ClaimShield' },
  },
  {
    path: '/approvals/:token',
    name: 'phase-approval',
    component: () => import('@views/PhaseApprovalView.vue'),
    meta: { requiresAuth: false, title: 'Driver Approval | ClaimShield' },
  },
  {
    path: '/r/:token',
    name: 'remediation',
    component: () => import('@views/RemediationView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Driver File Remediation | ClaimShield' },
  },
  {
    path: '/onboarding/expired',
    name: 'OnboardingExpired',
    component: () => import('@/views/OnboardingExpiredView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Link Expired | ClaimShield' },
  },
  {
    path: '/onboarding/:token/complete',
    name: 'OnboardingComplete',
    component: () => import('@/views/OnboardingCompleteView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Onboarding Complete | ClaimShield' },
  },
  {
    path: '/onboarding/:token',
    name: 'OnboardingWizard',
    component: () => import('@views/onboarding/OnboardingWizardView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Onboarding | ClaimShield' },
  },
  // Standalone single onboarding step — deep-linked from the CS Report
  // "Action Needed" rows. :step is a slug (driver-list | driver-files | driver-app).
  {
    path: '/setup/:token/:step',
    name: 'OnboardingStep',
    component: () => import('@views/onboarding/OnboardingStepView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Setup | ClaimShield' },
  },
  // Public driver-files uploader — deep-linked from the CS Report
  // "Driver File Setup" row. Works for any client (login-less, token-based).
  {
    path: '/upload/driver-files/:token',
    name: 'DriverFilesUpload',
    component: () => import('@views/onboarding/DriverFilesUploadView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Upload Driver Files | ClaimShield' },
  },
  // Legacy route — redirect to new wizard
  {
    path: '/onboarding-old/:token',
    name: 'onboarding-checklist',
    redirect: to => ({ name: 'OnboardingWizard', params: { token: to.params.token } }),
  },

  // Screening request routes (public - DOT-based, self-contained layout)
  {
    path: '/request/mvr/:dotNumber',
    name: 'mvr-request',
    component: () => import('@views/ScreeningRequestView.vue'),
    meta: { requiresAuth: false, title: 'MVR Request | ClaimShield' },
  },
  {
    path: '/request/mvr-psp/:dotNumber',
    name: 'mvr-psp-request',
    component: () => import('@views/ScreeningRequestView.vue'),
    meta: { requiresAuth: false, title: 'MVR + PSP Request | ClaimShield' },
  },
  // Survey routes (public - token-based, self-contained layout)
  {
    path: '/survey/:token',
    name: 'survey',
    component: () => import('@views/SurveyView.vue'),
    meta: { requiresAuth: false, title: 'Survey | ClaimShield' },
  },

  // Drug test location change (public - token-based, driver-facing)
  {
    path: '/test/location/:token',
    name: 'test-location-change',
    component: () => import('@views/test/TestLocationChange.vue'),
    meta: { requiresAuth: false, layout: 'minimal' as const, title: 'Find a Collection Site | ClaimShield' },
  },

  // FCRA adverse action dispute (public - token-based, driver-facing)
  {
    path: '/adverse-action/dispute/:token',
    name: 'AdverseActionDispute',
    component: () => import('@views/AdverseActionDisputeView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Dispute Employment Decision | ClaimShield' },
  },

  // D&A policy re-sign (public - token-based, driver-facing)
  {
    path: '/resign/:token',
    name: 'PolicyResign',
    component: () => import('@views/PolicyResignView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Drug & Alcohol Policy — ClaimShield' },
  },

  // Native document signing (public - token-based, generic envelope flow)
  {
    path: '/sign/:token',
    name: 'SignEnvelope',
    component: () => import('@views/sign/SignEnvelopeView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Sign Document — ClaimShield' },
  },

  // Safety policy acknowledgment (public - magic-link, driver-facing)
  {
    path: '/policy-ack/:token',
    name: 'PolicyAcknowledgment',
    component: () => import('@views/PolicyAcknowledgmentView.vue'),
    meta: { requiresAuth: false, public: true, title: 'Policy Acknowledgment — ClaimShield' },
  },

  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: ForgotPasswordView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Forgot Password | ClaimShield Portal' },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: ResetPasswordView,
    meta: { requiresAuth: false, layout: 'auth', title: 'Reset Password | ClaimShield Portal' },
  },

  // Portal routes (authenticated)
  {
    path: '/',
    component: PortalLayout,
    meta: { requiresAuth: true, roles: ['client', 'staff', 'admin'] },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@views/DashboardView.vue'),
        meta: { title: 'Dashboard | ClaimShield Portal' },
      },
      // Temporarily disabled - re-enable when ready
      // {
      //   path: 'compliance',
      //   name: 'compliance',
      //   component: () => import('@views/ComplianceView.vue'),
      //   meta: { title: 'Compliance | ClaimShield Portal' },
      // },
      {
        path: 'safety-scores',
        name: 'safety-scores',
        component: () => import('@views/SafetyScoresView.vue'),
        meta: { title: 'Safety Scores | ClaimShield Portal' },
      },
      {
        path: 'drivers',
        name: 'drivers',
        component: () => import('@views/DriversView.vue'),
        meta: { title: 'Drivers | ClaimShield Portal' },
      },
      {
        path: 'drivers/:id',
        name: 'driver-detail',
        component: () => import('@views/DriverDetailView.vue'),
        meta: { title: 'Driver Details | ClaimShield Portal' },
      },
      {
        path: 'vehicles',
        name: 'vehicles',
        component: () => import('@views/VehiclesView.vue'),
        meta: { title: 'Vehicles | ClaimShield Portal' },
      },
      {
        path: 'vehicles/:id/dvir/new',
        name: 'dvir-form',
        component: () => import('@views/DVIRFormView.vue'),
        meta: { title: 'New Inspection | ClaimShield Portal' },
      },
      {
        path: 'vehicles/:id/dvir/:dvirId',
        name: 'dvir-detail',
        component: () => import('@views/DVIRDetailView.vue'),
        meta: { title: 'Inspection Report | ClaimShield Portal' },
      },
      {
        path: 'vehicles/:id',
        name: 'vehicle-detail',
        component: () => import('@views/VehicleDetailView.vue'),
        meta: { title: 'Vehicle Details | ClaimShield Portal' },
      },
      {
        path: 'training',
        name: 'training',
        component: () => import('@views/TrainingView.vue'),
        meta: { title: 'Training | ClaimShield Portal' },
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@views/BillingView.vue'),
        meta: { title: 'Billing | ClaimShield Portal' },
      },
      {
        path: 'services',
        name: 'services',
        component: () => import('@views/ServicesView.vue'),
        meta: { title: 'Services | ClaimShield Portal' },
      },
      {
        path: 'documents',
        name: 'documents',
        component: () => import('@views/DocumentsView.vue'),
        meta: { title: 'Documents | ClaimShield Portal' },
      },
      {
        path: 'handbook',
        name: 'handbook',
        component: () => import('@views/HandbookBuilderView.vue'),
        meta: { title: 'Driver Handbook | ClaimShield Portal' },
      },
      // Temporarily disabled - re-enable when ready
      // {
      //   path: 'reports',
      //   name: 'reports',
      //   component: () => import('@views/ReportsView.vue'),
      //   meta: { title: 'Reports | ClaimShield Portal' },
      // },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@views/SettingsView.vue'),
        meta: { title: 'Settings | ClaimShield Portal' },
      },
      {
        path: 'contacts',
        name: 'contacts',
        component: () => import('@views/ContactsView.vue'),
        meta: { title: 'Notification Contacts | ClaimShield Portal' },
      },
      {
        path: 'dashboard-v2',
        name: 'dashboard-v2',
        component: () => import('@views/DashboardPrototypeView.vue'),
        meta: { title: 'Dashboard | ClaimShield Portal' },
      },
    ],
  },

  // Logout — clear auth then redirect to login
  {
    path: '/logout',
    name: 'logout',
    beforeEnter: () => {
      const { clearAuth } = useAuthStore()
      clearAuth()
      return '/login'
    },
    component: LoginView,
  },

  // 404 catch-all
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: { requiresAuth: false, title: 'Not Found | ClaimShield Portal' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

// Setup navigation guards
setupGuards(router)
